import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import Tour from '../components/Tour';
import NotionExportButton from '../components/NotionExportButton';
import TrelloExportButton from '../components/TrelloExportButton';
import ClickUpExportButton from '../components/ClickUpExportButton';
import {
  Palette, Sparkle, Globe, Tag, Copy, Download, Trash, Lightning, ArrowRight, Code,
  Quotes, Question, Check, Warning, Trophy, Share, Link as LinkIcon,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt', label: 'Português (PT + BR)' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

const FORMAT_CODES = ['', 'course', 'ebook', 'saas', 'mentorship', 'community'];

const copyToClipboard = async (text, label, errLabel = 'Copy failed') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error(errLabel);
  }
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Subcomponents ───────────────────────────────────────────────

const SectionCard = ({ icon: Icon, title, color = '#A855F7', children, actions }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <h3 className="font-[Outfit] font-bold">{title}</h3>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const BrandView = ({ brand }) => {
  const { t } = useTranslation();
  if (!brand) return null;
  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl border bg-gradient-to-br from-[#A855F7]/15 to-transparent border-[#A855F7]/40">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#A855F7] mb-2">{t('brd.rec_name')}</div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-4xl font-[Outfit] font-bold">{brand.recommended_name}</div>
          <button data-testid="copy-brand-name"
            onClick={() => copyToClipboard(brand.recommended_name, t('brd.name_copied'), t('brd.copy_fail'))}
            className="p-1.5 rounded hover:bg-white/10"><Copy size={14} /></button>
        </div>
        {brand.tagline && <div className="text-base text-white/80 mt-2 italic">&ldquo;{brand.tagline}&rdquo;</div>}
        {brand.slogan && <div className="text-sm text-white/60 mt-1">{brand.slogan}</div>}
      </div>

      {Array.isArray(brand.name_suggestions) && brand.name_suggestions.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.alternatives')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {brand.name_suggestions.map((n, i) => (
              <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-[Outfit] font-bold text-sm">{n.name}</span>
                  {n.vibe && <span className="px-2 py-0.5 bg-white/10 rounded text-[10px] uppercase tracking-wider text-white/60">{n.vibe}</span>}
                </div>
                <p className="text-xs text-white/60 mt-1">{n.rationale}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {brand.voice_tone && (
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('brd.brand_voice')}</div>
          <p className="text-sm text-white/85">{brand.voice_tone}</p>
        </div>
      )}

      {Array.isArray(brand.color_palette) && brand.color_palette.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.palette')}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {brand.color_palette.map((c, i) => (
              <button key={i} data-testid={`copy-color-${c.role}`}
                onClick={() => copyToClipboard(c.hex, t('brd.hex_copied', { hex: c.hex }), t('brd.copy_fail'))}
                className="p-3 bg-white/5 border border-white/10 rounded-lg text-left hover:bg-white/10 transition">
                <div className="w-full h-12 rounded mb-2" style={{ background: c.hex }} />
                <div className="text-[10px] uppercase tracking-wider text-white/40">{c.role}</div>
                <div className="text-xs font-semibold">{c.name}</div>
                <div className="text-[10px] text-white/50 font-mono">{c.hex}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {brand.font_pairing && (
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Heading</div>
            <div className="text-base font-semibold">{brand.font_pairing.heading}</div>
          </div>
          <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Body</div>
            <div className="text-base font-semibold">{brand.font_pairing.body}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const DomainsView = ({ domains }) => {
  const { t } = useTranslation();
  if (!Array.isArray(domains) || domains.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {domains.map((d, i) => (
        <a key={i} href={`https://www.namecheap.com/domains/registration/results/?domain=${encodeURIComponent(d.domain)}`}
          target="_blank" rel="noopener noreferrer"
          data-testid={`domain-link-${i}`}
          className="p-3 bg-white/5 border border-white/10 rounded-lg flex items-center justify-between hover:border-[#2563EB]/50 transition group">
          <div>
            <div className="font-mono text-sm font-semibold">{d.domain}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{d.memorability} {t('brd.memorability')}</div>
          </div>
          <ArrowRight size={14} className="text-white/40 group-hover:text-[#2563EB] transition" />
        </a>
      ))}
    </div>
  );
};

const SalesPageView = ({ sp }) => {
  const { t } = useTranslation();
  if (!sp) return null;
  const hero = sp.hero || {};
  const closing = sp.closing_cta || {};
  const guarantee = sp.guarantee || {};
  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="p-5 bg-gradient-to-br from-[#2563EB]/15 to-transparent border border-[#2563EB]/40 rounded-xl">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#2563EB] font-semibold mb-2">Hero</div>
        <h2 className="text-2xl md:text-3xl font-[Outfit] font-bold leading-tight mb-2">{hero.headline}</h2>
        <p className="text-base text-white/80 mb-3">{hero.subheadline}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {hero.cta_primary && <span className="px-4 py-2 bg-[#2563EB] rounded-lg text-sm font-semibold">{hero.cta_primary}</span>}
          {hero.cta_secondary && <span className="px-4 py-2 bg-white/5 border border-white/15 rounded-lg text-sm font-semibold">{hero.cta_secondary}</span>}
          <button data-testid="copy-hero"
            onClick={() => copyToClipboard(`${hero.headline}\n\n${hero.subheadline}`, t('brd.hero_copied'), t('brd.copy_fail'))}
            className="ml-auto p-1.5 rounded hover:bg-white/10"><Copy size={14} /></button>
        </div>
      </div>

      {/* Pains */}
      {Array.isArray(sp.pain_points) && sp.pain_points.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.pains')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sp.pain_points.map((p, i) => (
              <div key={i} className="p-3 bg-[#EF4444]/8 border border-[#EF4444]/25 rounded-lg flex items-start gap-2 text-sm">
                <span className="text-lg flex-shrink-0">{p.icon_hint || '!'}</span>
                <span className="text-white/85">{p.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Solution */}
      {sp.solution_intro && (
        <div className="p-4 bg-white/5 border-l-4 border-[#10B981] rounded-r-lg">
          <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-semibold mb-1">{t('brd.solution')}</div>
          <p className="text-sm text-white/85 leading-relaxed">{sp.solution_intro}</p>
        </div>
      )}

      {/* Features/Benefits */}
      {Array.isArray(sp.features_benefits) && sp.features_benefits.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.features_benefits')}</div>
          <div className="space-y-2">
            {sp.features_benefits.map((f, i) => (
              <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="flex items-start gap-2">
                  <Check size={14} weight="bold" className="text-[#10B981] flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm font-semibold">{f.feature}</div>
                    <p className="text-xs text-white/60 mt-0.5">{f.benefit}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social proof angles */}
      {Array.isArray(sp.social_proof_angles) && sp.social_proof_angles.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.social_proof')}</div>
          <div className="space-y-2">
            {sp.social_proof_angles.map((s, i) => (
              <div key={i} className="p-3 bg-white/5 border-l-2 border-[#F59E0B] rounded-r-lg flex items-start gap-2 text-sm">
                <Quotes size={14} className="text-[#F59E0B] flex-shrink-0 mt-1" weight="bold" />
                <span className="text-white/80">{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Urgency */}
      {Array.isArray(sp.urgency_hooks) && sp.urgency_hooks.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{t('brd.urgency')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sp.urgency_hooks.map((u, i) => (
              <div key={i} className="p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg flex items-start gap-2 text-sm">
                <Lightning size={14} weight="bold" className="text-[#F59E0B] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-semibold">{u.type}</div>
                  <span className="text-white/85">{u.copy}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guarantee */}
      {(guarantee.headline || guarantee.body) && (
        <div className="p-4 bg-gradient-to-br from-[#10B981]/12 to-transparent border border-[#10B981]/35 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={16} weight="bold" className="text-[#10B981]" />
            <div className="text-sm font-[Outfit] font-bold text-[#10B981]">{guarantee.headline}</div>
          </div>
          <p className="text-sm text-white/80">{guarantee.body}</p>
        </div>
      )}

      {/* FAQ */}
      {Array.isArray(sp.faq) && sp.faq.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">FAQ</div>
          <div className="space-y-1.5">
            {sp.faq.map((q, i) => (
              <details key={i} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg group">
                <summary className="cursor-pointer text-sm font-semibold flex items-start gap-2">
                  <Question size={14} weight="bold" className="text-[#A855F7] flex-shrink-0 mt-0.5" />
                  <span className="flex-1">{q.question}</span>
                </summary>
                <p className="text-xs text-white/70 mt-2 ml-6 leading-relaxed">{q.answer}</p>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Closing CTA */}
      {(closing.headline || closing.body) && (
        <div className="p-5 bg-gradient-to-br from-[#2563EB]/15 to-[#A855F7]/10 border border-[#2563EB]/40 rounded-xl text-center">
          <h3 className="text-xl font-[Outfit] font-bold mb-2">{closing.headline}</h3>
          <p className="text-sm text-white/75 mb-4">{closing.body}</p>
          {closing.button && (
            <span className="inline-block px-6 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-lg text-sm font-bold">{closing.button}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────

export default function Branding() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const sourceBlueprintId = params.get('blueprint_id');
  const sourceBuildId = params.get('build_id');

  const [form, setForm] = useState({
    niche: '',
    target_audience: '',
    product_format: '',
    unique_value_prop: '',
    price_eur: '',
    language: 'pt',
    source_blueprint_id: sourceBlueprintId || '',
    source_build_id: sourceBuildId || '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/branding/history`);
      setHistory(data.items || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    const h = () => loadHistory();
    window.addEventListener('brand:changed', h);
    return () => window.removeEventListener('brand:changed', h);
  }, [loadHistory]);

  const onGenerate = async (e) => {
    e?.preventDefault?.();
    if (!form.niche.trim() || !form.target_audience.trim()) {
      toast.error(t('brd.err_required'));
      return;
    }
    const startedAt = Date.now();
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        price_eur: form.price_eur ? Number(form.price_eur) : null,
        source_blueprint_id: form.source_blueprint_id || null,
        source_build_id: form.source_build_id || null,
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/branding/generate`, payload, { timeout: 120000 });
      setResult(data);
      toast.success('Branding gerado');
      loadHistory();
    } catch (err) {
      const status = err.response?.status;
      const isTimeout = err.code === 'ECONNABORTED' || status === 502 || status === 504;
      if (isTimeout) {
        toast.info('Resposta demorou — a verificar se o kit foi guardado…');
        const recovered = await _pollForRecentBranding(form.niche, startedAt);
        if (recovered) {
          setResult(recovered);
          toast.success('Kit recuperado');
          loadHistory();
          setLoading(false);
          return;
        }
      }
      const detail = err.response?.data?.detail || 'Falha a gerar branding';
      toast.error(detail);
      if (status === 429) {
        setTimeout(() => navigate('/pricing?utm_source=branding_quota'), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const _pollForRecentBranding = async (niche, startedAt) => {
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/branding/history`);
        const items = data.items || [];
        const match = items.find((x) => x.niche === niche && new Date(x.created_at).getTime() >= startedAt - 2000);
        if (match) {
          const detail = await axios.get(`${BACKEND_URL}/api/branding/${match.branding_id}`);
          return detail.data;
        }
      } catch { /* swallow & retry */ }
    }
    return null;
  };

  const loadFromHistory = async (id) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/branding/${id}`);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error(t('brd.load_fail'));
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/branding/${id}`);
      setHistory((h) => h.filter((x) => x.branding_id !== id));
      if (result?.branding_id === id) setResult(null);
      toast.success(t('brd.deleted'));
    } catch {
      toast.error(t('brd.delete_fail'));
    }
  };

  const exportHtml = (id) => {
    // Open server-rendered HTML in a new tab; user can use browser "Save As" to download.
    const url = `${BACKEND_URL}/api/branding/${id}/export`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [publishing, setPublishing] = useState(false);
  const publishLanding = async () => {
    if (!result?.branding_id) return;
    setPublishing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/branding/${result.branding_id}/publish`, {});
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setResult((r) => ({ ...r, published: { ...data, url: data.url } }));
      await copyToClipboard(fullUrl, t('brd.pub_link_copied'), t('brd.copy_fail'));
      toast.success(data.reused ? t('brd.already_pub') : t('brd.published_ok'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('brd.pub_fail'));
    } finally {
      setPublishing(false);
    }
  };

  const unpublishLanding = async () => {
    if (!result?.branding_id) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/branding/${result.branding_id}/publish`);
      setResult((r) => { const c = { ...r }; delete c.published; return c; });
      toast.success(t('brd.unpublished'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('brd.unpub_fail'));
    }
  };

  const kit = result?.kit || {};

  return (
    <DashboardLayout>
      <Tour tourId="branding-v1" steps={[
        { target: '[data-testid="input-niche"]', content: t('brd.tour_1'), disableBeacon: true },
        { target: '[data-testid="input-audience"]', content: t('brd.tour_2') },
        { target: '[data-testid="btn-generate"]', content: t('brd.tour_3') },
      ]} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">Branding & Sales Page</h1>
          <p className="text-white/60 text-sm mt-1">{t('brd.subtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={onGenerate} className="p-5 bg-[#121212] border border-white/10 rounded-2xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.niche')}</label>
              <input data-testid="input-niche" value={form.niche}
                onChange={(e) => setForm({ ...form, niche: e.target.value })}
                placeholder={t('brd.niche_ph')}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.audience')}</label>
              <input data-testid="input-audience" value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                placeholder={t('brd.audience_ph')}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.format_label')}</label>
              <select data-testid="select-format" value={form.product_format}
                onChange={(e) => setForm({ ...form, product_format: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]">
                {FORMAT_CODES.map((f) => <option key={f} value={f}>{t(`brd.format_${f || 'none'}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.language')}</label>
              <select data-testid="select-language" value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]">
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.usp_label')}</label>
              <input data-testid="input-usp" value={form.unique_value_prop}
                onChange={(e) => setForm({ ...form, unique_value_prop: e.target.value })}
                placeholder={t('brd.usp_ph')}
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50">{t('brd.price_label')}</label>
              <input data-testid="input-price" type="number" min="0" value={form.price_eur}
                onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                placeholder="97"
                className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]" />
            </div>
          </div>
          <button data-testid="btn-generate" type="submit" disabled={loading}
            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-[#A855F7] to-[#2563EB] rounded-xl font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Sparkle size={16} weight="bold" className="animate-pulse" /> {t('brd.generating')}</> : <><Palette size={16} weight="bold" /> {t('brd.gen_btn')}</>}
          </button>
          {(form.source_blueprint_id || form.source_build_id) && (
            <div className="text-xs text-white/50 flex items-center gap-2">
              <Lightning size={12} weight="bold" className="text-[#A855F7]" />
              {t('brd.ctx_pre')} {form.source_blueprint_id ? t('brd.ctx_bp') : t('brd.ctx_ab')} {t('brd.ctx_post')}
            </div>
          )}
        </form>

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="branding-result">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t('brd.result_label')} · {result.niche}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="btn-publish-landing"
                  onClick={publishLanding} disabled={publishing}
                  className="px-3 py-1.5 bg-gradient-to-r from-[#10B981] to-[#34D399] rounded-lg text-xs font-bold text-[#052e1d] hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
                  <Share size={12} weight="bold" /> {publishing ? t('brd.publishing') : (result.published ? t('brd.republish') : t('brd.publish_landing'))}
                </button>
                <button data-testid="btn-export-html"
                  onClick={() => exportHtml(result.branding_id)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 flex items-center gap-1.5">
                  <Code size={12} weight="bold" /> {t('brd.view_html')}
                </button>
                <button data-testid="btn-export-json"
                  onClick={() => downloadJson(result, `branding-${result.branding_id}.json`)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-white/10 flex items-center gap-1.5">
                  <Download size={12} weight="bold" /> JSON
                </button>
                <NotionExportButton
                  shape_type="branding"
                  source_id={result.branding_id}
                  testId="btn-export-notion-branding"
                />
                <TrelloExportButton
                  shape_type="branding"
                  source_id={result.branding_id}
                  testId="btn-export-trello-branding"
                />
                <ClickUpExportButton
                  shape_type="branding"
                  source_id={result.branding_id}
                  testId="btn-export-clickup-branding"
                />
              </div>
            </div>

            {result.published && (
              <div className="p-3 bg-gradient-to-br from-[#10B981]/15 to-transparent border border-[#10B981]/35 rounded-xl flex items-center gap-3 flex-wrap" data-testid="published-banner">
                <LinkIcon size={16} weight="bold" className="text-[#10B981] flex-shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-bold">{t('brd.public_page')}</div>
                  <a href={`${BACKEND_URL}${result.published.url}`} target="_blank" rel="noopener noreferrer"
                    className="text-sm font-mono text-white hover:text-[#10B981] break-all">
                    {BACKEND_URL}{result.published.url}
                  </a>
                </div>
                <button data-testid="btn-copy-published-url"
                  onClick={() => copyToClipboard(`${BACKEND_URL}${result.published.url}`, t('brd.link_copied'), t('brd.copy_fail'))}
                  className="p-2 rounded hover:bg-white/10"><Copy size={14} /></button>
                <button data-testid="btn-unpublish"
                  onClick={unpublishLanding}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-semibold hover:bg-[#EF4444]/15 hover:border-[#EF4444]/40">
                  {t('brd.unpublish_btn')}
                </button>
              </div>
            )}

            <SectionCard icon={Tag} title={t('brd.sec_brand')} color="#A855F7">
              <BrandView brand={kit.brand} />
            </SectionCard>

            <SectionCard icon={Globe} title={t('brd.sec_domains')} color="#2563EB">
              <DomainsView domains={kit.domains} />
            </SectionCard>

            <SectionCard icon={Sparkle} title={t('brd.sec_sales')} color="#10B981">
              <SalesPageView sp={kit.sales_page} />
            </SectionCard>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <SectionCard icon={Tag} title={t('brd.history')} color="#666">
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.branding_id} className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg flex items-center justify-between gap-2">
                  <button data-testid={`history-item-${h.branding_id}`}
                    onClick={() => loadFromHistory(h.branding_id)}
                    className="flex-1 text-left hover:opacity-80 transition">
                    <div className="font-[Outfit] font-bold text-sm">{h.kit?.brand?.recommended_name || t('brd.no_name')}</div>
                    <div className="text-xs text-white/50 mt-0.5">{h.niche} · {new Date(h.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}</div>
                  </button>
                  <button data-testid={`history-delete-${h.branding_id}`}
                    onClick={() => deleteItem(h.branding_id)}
                    className="p-1.5 rounded text-white/40 hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {!result && history.length === 0 && (
          <div className="p-8 bg-[#121212] border border-dashed border-white/15 rounded-2xl text-center">
            <Palette size={32} weight="bold" className="text-[#A855F7] mx-auto mb-3" />
            <h3 className="font-[Outfit] font-bold text-lg mb-1">{t('brd.empty_title')}</h3>
            <p className="text-sm text-white/60 max-w-md mx-auto">{t('brd.empty_sub')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
